import { useState, useEffect } from 'react';
import { FileText, Calendar, Clock, ChevronRight, Search, RefreshCw, Download, Mail, Share2, X, ExternalLink, Cloud, CloudOff, Loader2, Timer, Sparkles, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Card, Badge, Input, LoadingSpinner, Alert, TaskProgressPanel, TruncatedText } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { usePreferences } from '@/hooks/usePreferences';
import { useActiveTask } from '@/hooks/useActiveTask';
import { useProgressStream } from '@/hooks/useProgressStream';
import { api } from '@/lib/api';
import { formatDateInTimezone, formatTimeInTimezone, formatFullDateInTimezone, getElapsedMinutes } from '@/lib/dateUtils';
import type { Newsletter } from '@/types';

interface NewsletterWithS3 extends Newsletter {
  s3_url?: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
}

/**
 * Format duration in seconds to a human-readable string.
 * Examples: "45s", "2m 30s", "1h 5m"
 */
function formatDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null) return '';

  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}

/**
 * Get generation stage info based on elapsed minutes.
 */
function getGenerationStageInfo(elapsedMinutes: number): { stage: string; icon: React.ReactNode; progress: number } {
  if (elapsedMinutes < 5) {
    return { stage: 'Initializing...', icon: <Loader2 className="w-4 h-4 animate-spin" />, progress: 10 };
  } else if (elapsedMinutes < 15) {
    return { stage: 'Researching...', icon: <Search className="w-4 h-4" />, progress: 30 };
  } else if (elapsedMinutes < 25) {
    return { stage: 'Writing...', icon: <FileText className="w-4 h-4" />, progress: 55 };
  } else if (elapsedMinutes < 35) {
    return { stage: 'Fact-checking...', icon: <CheckCircle2 className="w-4 h-4" />, progress: 75 };
  } else {
    return { stage: 'Finalizing...', icon: <Sparkles className="w-4 h-4" />, progress: 90 };
  }
}

/**
 * Format the current_phase from backend into a user-friendly display string.
 */
function formatPhaseDisplay(phase: string): string {
  // Map backend phase names to user-friendly strings
  const phaseMap: Record<string, string> = {
    'starting': 'Starting...',
    'analyzing_prompt': 'Analyzing your request...',
    'task_breakdown_complete': 'Planning research tasks...',
    'final_aggregation': 'Finalizing report...',
    'done': 'Complete',
  };

  // Check for exact match first
  if (phaseMap[phase]) {
    return phaseMap[phase];
  }

  // Handle executing_task_N pattern
  const taskMatch = phase.match(/executing_task_(\d+)/);
  if (taskMatch) {
    return `Executing Task ${taskMatch[1]}...`;
  }

  // Fallback: format snake_case to Title Case
  return phase
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Collapsible prompt component - shows a truncated prompt with expand/collapse toggle.
 */
function CollapsiblePrompt({ prompt, className = '' }: { prompt: string; className?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const truncateLength = 100;
  const shouldTruncate = prompt.length > truncateLength;
  const displayText = shouldTruncate && !isExpanded
    ? prompt.slice(0, truncateLength) + '...'
    : prompt;

  return (
    <div className={`mb-3 ${className}`}>
      <div className="text-xs text-text-muted mb-1 font-medium">Prompt:</div>
      <div className="bg-surface-light rounded p-2 text-xs text-text-secondary italic">
        {displayText}
        {shouldTruncate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="ml-2 text-primary hover:underline"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
}

export function HistoryPage() {
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNewsletter, setSelectedNewsletter] = useState<NewsletterWithS3 | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [expandedProgressId, setExpandedProgressId] = useState<string | null>(null);

  const isMasterUser = user?.is_master_user ?? false;
  // Get user's timezone from preferences, fallback to browser timezone
  const userTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Track active tasks to auto-refresh when completed
  const { activeTasks, hasActiveTasks } = useActiveTask({
    pollInterval: 5000,
    onComplete: () => {
      // Refresh the list when a task completes
      fetchNewsletters();
    }
  });

  // Build a map of history_id -> active task for quick lookup
  const activeTaskMap = new Map(
    activeTasks.map(task => [task.history_id, task])
  );

  // Get the first active task's history_id for real-time progress streaming (SSE limitation: one stream)
  // Note: SSE streaming only works for one task at a time, but polled progress works for all
  const activeHistoryId = activeTasks[0]?.history_id;

  // Real-time progress streaming via SSE (for first active task only)
  const { progress: realtimeProgress } = useProgressStream(activeHistoryId || null, {
    enabled: hasActiveTasks && !!activeHistoryId,
    onComplete: () => {
      // SSE stream completed - refresh to get final state
      fetchNewsletters();
    }
  });

  const fetchNewsletters = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getNewsletterHistory({ limit: 50 });
      setNewsletters(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const handleViewNewsletter = async (newsletter: Newsletter) => {
    // Set initial state with newsletter data (which already has s3_url from history)
    setSelectedNewsletter(newsletter as NewsletterWithS3);
    setIsLoadingDetails(true);
    try {
      const detail = await api.getNewsletterDetail(newsletter.id);
      // Merge detail with newsletter, preferring s3_url from detail if available, otherwise keep original
      setSelectedNewsletter({
        ...newsletter,
        ...detail,
        s3_url: detail.s3_url || newsletter.s3_url
      });
    } catch (err) {
      console.error('Failed to load newsletter details:', err);
      // Even on error, keep the original newsletter data with s3_url
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleOpenFullReport = () => {
    if (selectedNewsletter?.s3_url) {
      window.open(selectedNewsletter.s3_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownload = () => {
    // Direct download from S3 URL
    if (!selectedNewsletter?.s3_url) return;
    const a = document.createElement('a');
    a.href = selectedNewsletter.s3_url;
    a.download = `${selectedNewsletter.subject || 'newsletter'}.html`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    if (!selectedNewsletter) return;
    // Share the S3 URL if available, otherwise share the current page
    const shareUrl = selectedNewsletter.s3_url || window.location.href;
    const shareData = {
      title: selectedNewsletter.subject || 'Newsletter',
      text: selectedNewsletter.content_summary || 'Check out this newsletter from Verdict!',
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy S3 URL to clipboard
      await navigator.clipboard.writeText(shareUrl);
      alert('Report link copied to clipboard!');
    }
  };

  const handleEmailShare = () => {
    if (!selectedNewsletter) return;
    const subject = encodeURIComponent(selectedNewsletter.subject || 'Newsletter');
    const reportLink = selectedNewsletter.s3_url ? `\n\nView full report: ${selectedNewsletter.s3_url}` : '';
    const body = encodeURIComponent(`Check out this newsletter:\n\n${selectedNewsletter.content_summary || ''}${reportLink}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleTwitterShare = () => {
    if (!selectedNewsletter) return;
    const reportUrl = selectedNewsletter.s3_url || '';
    const text = encodeURIComponent(`${selectedNewsletter.subject || 'Newsletter'}${reportUrl ? `\n\n${reportUrl}` : ''}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'width=550,height=420');
  };

  const handleLinkedInShare = () => {
    if (!selectedNewsletter) return;
    const shareUrl = selectedNewsletter.s3_url || window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=550,height=420');
  };

  const handleFacebookShare = () => {
    if (!selectedNewsletter) return;
    const shareUrl = selectedNewsletter.s3_url || window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=550,height=420');
  };

  const filteredNewsletters = newsletters.filter((n) =>
    n.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.content_summary?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a newsletter has been stuck in pending state for too long (> 30 minutes)
  const isStaleInProgress = (newsletter: Newsletter) => {
    if (newsletter.status !== 'queued' && newsletter.status !== 'generating') {
      return false;
    }
    const createdAt = new Date(newsletter.generated_at || newsletter.created_at);
    const now = new Date();
    const minutesSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    return minutesSinceCreation > 30; // Stale if pending for more than 30 minutes
  };

  const getStatusBadge = (newsletter: Newsletter) => {
    const { status } = newsletter;
    // Check for stale in-progress newsletters
    if (isStaleInProgress(newsletter)) {
      return <Badge variant="error">Issue Detected</Badge>;
    }
    switch (status) {
      case 'sent':
        return <Badge variant="success">Sent</Badge>;
      case 'generated':
        return <Badge variant="success">Generated</Badge>;
      case 'queued':
        return <Badge variant="info">Queued</Badge>;
      case 'generating':
        return <Badge variant="warning">Generating...</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'failed':
        return <Badge variant="error">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Check if newsletter is still in progress (and not stale)
  const isInProgress = (newsletter: Newsletter) => {
    if (newsletter.status !== 'queued' && newsletter.status !== 'generating') {
      return false;
    }
    // If it's stale, treat it as no longer in progress (so user can click and see the error)
    return !isStaleInProgress(newsletter);
  };

  // Extract topic from subject line (e.g., "Research Newsletter: Topic Name" -> "Topic Name")
  const extractTopic = (newsletter: Newsletter) => {
    if (newsletter.topic) return newsletter.topic;
    if (newsletter.subject) {
      const match = newsletter.subject.match(/Newsletter[:\s-]+(.+)$/i);
      if (match) return match[1].trim();
      return newsletter.subject;
    }
    return 'Custom';
  };

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="h-7 w-48 bg-surface-light/50 rounded animate-pulse" />
            <div className="h-4 w-64 bg-surface-light/30 rounded animate-pulse mt-2" />
          </div>
          <div className="h-9 w-24 bg-surface-light/30 rounded-lg animate-pulse" />
        </div>
        <div className="h-10 w-full bg-surface-light/30 rounded-lg animate-pulse" />
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-surface-light/50 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 bg-surface-light/50 rounded animate-pulse" />
                  <div className="h-3 w-64 bg-surface-light/30 rounded animate-pulse" />
                  <div className="flex gap-4">
                    <div className="h-3 w-20 bg-surface-light/30 rounded animate-pulse" />
                    <div className="h-3 w-16 bg-surface-light/30 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-6 w-16 bg-surface-light/30 rounded-full animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Count newsletters with/without S3 URLs
  const s3Stats = {
    withUrl: newsletters.filter(n => n.s3_url).length,
    withoutUrl: newsletters.filter(n => !n.s3_url).length,
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Newsletter History</h1>
          <p className="text-text-secondary text-sm sm:text-base mt-1">
            View your past newsletters
            {isMasterUser && newsletters.length > 0 && (
              <span className="ml-2 text-xs">
                ({s3Stats.withUrl} with full report, {s3Stats.withoutUrl} without)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={fetchNewsletters} leftIcon={<RefreshCw className="w-4 h-4" />} className="w-full sm:w-auto">
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Input
        placeholder="Search newsletters..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        leftIcon={<Search className="w-5 h-5" />}
      />

      {filteredNewsletters.length === 0 ? (
        <Card className="text-center py-12">
          <FileText className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No newsletters yet</h3>
          <p className="text-text-secondary">
            Your newsletter history will appear here once you receive your first newsletter.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredNewsletters.map((newsletter) => {
            const inProgress = isInProgress(newsletter);
            const stale = isStaleInProgress(newsletter);

            // Find the matching active task for THIS newsletter (not just the first one)
            const matchingActiveTask = activeTaskMap.get(newsletter.id);
            const isActiveNewsletter = inProgress && !!matchingActiveTask;

            // Calculate elapsed time for in-progress newsletters
            // Use server-calculated elapsed time if available, otherwise calculate client-side
            const clientElapsedMinutes = inProgress
              ? getElapsedMinutes(newsletter.generated_at || newsletter.created_at)
              : 0;
            const elapsedMinutes = isActiveNewsletter && matchingActiveTask?.elapsed_minutes !== undefined
              ? matchingActiveTask.elapsed_minutes
              : clientElapsedMinutes;
            const stageInfo = inProgress ? getGenerationStageInfo(elapsedMinutes) : null;

            // Get polled progress from the matching active task (each task has its own progress)
            const polledProgress = matchingActiveTask?.progress;

            // Check if this newsletter has real-time progress data from SSE
            // Note: SSE only works for the first active task due to browser/API limitation
            const hasRealtimeData = inProgress && activeHistoryId === newsletter.id && realtimeProgress;
            // Has full task breakdown with sub_tasks
            const hasTaskBreakdown = hasRealtimeData && realtimeProgress.sub_tasks && realtimeProgress.sub_tasks.length > 0;
            // Has initial progress (status is generating, even if no tasks yet)
            const hasInitialProgress = hasRealtimeData && realtimeProgress.status === 'generating';
            // Has polled progress from API (works for ALL active tasks)
            const hasPolledData = inProgress && polledProgress && polledProgress.total_steps > 0;

            // Get active task data for additional details (use matching task, not just first)
            const activeTaskData = matchingActiveTask || null;

            const completedTasks = realtimeProgress?.sub_tasks?.filter(t => t.status === 'completed').length || 0;
            const totalTasks = realtimeProgress?.total_tasks || polledProgress?.total_steps || matchingActiveTask?.total_tasks || 0;
            const currentStep = polledProgress?.step || (matchingActiveTask?.current_task_index ?? -1) + 1 || 0;
            const realProgress = hasTaskBreakdown
              ? (totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0)
              : hasPolledData
                ? (polledProgress.total_steps > 0 ? (currentStep / polledProgress.total_steps) * 100 : 0)
                : totalTasks > 0
                  ? (currentStep / totalTasks) * 100
                  : 0;

            // Calculate display progress:
            // - If we have task breakdown from SSE, use real progress
            // - If we have polled progress from API, use that
            // - If we have active task with total_tasks, calculate from that
            // - If we have initial progress (analyzing), show 5% to indicate real connection
            // - Otherwise fall back to time-based estimation
            const displayProgress = hasTaskBreakdown
              ? realProgress
              : hasPolledData
                ? realProgress
                : (totalTasks > 0 && currentStep > 0)
                  ? realProgress
                  : hasInitialProgress
                    ? 5
                    : (stageInfo?.progress || 0);

            // For display purposes, show real progress if we have SSE data, polled data, or active task with tasks
            const hasRealTimeProgress = hasTaskBreakdown || hasPolledData || (totalTasks > 0 && currentStep > 0);
            const isExpanded = expandedProgressId === newsletter.id;

            return (
            <Card
              key={newsletter.id}
              hover={!inProgress}
              onClick={() => !inProgress && handleViewNewsletter(newsletter)}
              className={inProgress ? 'border-primary/30 bg-gradient-to-r from-primary/5 to-transparent' : 'cursor-pointer'}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    stale ? 'bg-error/10' : inProgress ? 'bg-primary/20 animate-pulse' : 'bg-primary/10'
                  }`}>
                    {stale ? (
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-error" />
                    ) : inProgress && stageInfo ? (
                      stageInfo.icon
                    ) : (
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <TruncatedText text={extractTopic(newsletter)} as="h3" className="font-medium text-sm sm:text-base max-w-[200px] sm:max-w-none" />
                      {inProgress && (
                        <>
                          <span className="text-xs text-text-muted">
                            {elapsedMinutes < 60 ? `${elapsedMinutes}m` : `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m`}
                          </span>
                          {hasRealTimeProgress && (
                            <span className="text-xs text-primary">
                              {hasTaskBreakdown ? `${completedTasks}/${totalTasks}` : `${currentStep}/${totalTasks}`} tasks
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {inProgress && stageInfo ? (
                      <div className="mt-1">
                        {/* Show detailed task progress like admin view */}
                        {activeTaskData?.current_task_name ? (
                          <p className="text-xs text-primary font-medium">
                            Task {(activeTaskData.current_task_index ?? 0) + 1}/{activeTaskData.total_tasks ?? 0}: {activeTaskData.current_task_name}
                          </p>
                        ) : (
                          <p className="text-xs text-primary font-medium">
                            {/* Show real-time phase if we have SSE data, polled data, or fallback to time-based estimation */}
                            {hasRealtimeData && realtimeProgress?.current_phase
                              ? formatPhaseDisplay(realtimeProgress.current_phase)
                              : hasPolledData && polledProgress?.stage_label
                                ? polledProgress.stage_label
                                : activeTaskData?.current_phase
                                  ? formatPhaseDisplay(activeTaskData.current_phase)
                                  : stageInfo.stage}
                          </p>
                        )}
                        {/* Started time and elapsed */}
                        <div className="flex items-center gap-4 mt-1 text-xs text-text-muted flex-wrap">
                          {activeTaskData?.started_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Started: {formatTimeInTimezone(activeTaskData.started_at, userTimezone)}
                            </span>
                          )}
                          <span className={elapsedMinutes > 30 ? 'text-warning' : ''}>
                            Elapsed: {elapsedMinutes} min
                          </span>
                        </div>
                        {/* Task ID (customer can see this for reference) */}
                        {activeTaskData?.task_id && (
                          <div className="flex items-center gap-4 mt-1.5 text-xs text-text-muted">
                            <span className="font-mono bg-surface-light px-1.5 py-0.5 rounded">
                              Task: {activeTaskData.task_id}
                            </span>
                          </div>
                        )}
                        {/* Progress bar */}
                        <div className="mt-2 w-full max-w-xs bg-surface-light rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${displayProgress}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-text-muted">
                            {hasTaskBreakdown || hasPolledData || (activeTaskData?.total_tasks && activeTaskData.total_tasks > 0)
                              ? `${Math.round(displayProgress)}% complete`
                              : hasInitialProgress
                                ? 'Analyzing your request...'
                                : 'Reports typically take 5-20 minutes'}
                          </p>
                          {(hasRealTimeProgress || activeTaskData?.custom_prompt) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedProgressId(isExpanded ? null : newsletter.id);
                              }}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              {isExpanded ? 'Hide' : 'Show'} details
                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-text-muted line-clamp-1 max-w-md">
                          {stale
                            ? 'Generation encountered an issue. Our team has been notified.'
                            : (newsletter.subject || newsletter.content_summary)}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-text-secondary mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDateInTimezone(newsletter.generated_at || newsletter.created_at, userTimezone)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTimeInTimezone(newsletter.generated_at || newsletter.created_at, userTimezone)}
                          </span>
                          {newsletter.duration_seconds && (
                            <span className="flex items-center gap-1" title="Generation time">
                              <Timer className="w-4 h-4" />
                              {formatDuration(newsletter.duration_seconds)}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* S3 URL indicator - only show for completed newsletters */}
                  {!inProgress && !stale && (
                    newsletter.s3_url ? (
                      <span title="Full report available">
                        <Cloud className="w-4 h-4 text-green-500" />
                      </span>
                    ) : (
                      <span title="Full report not available">
                        <CloudOff className="w-4 h-4 text-text-muted" />
                      </span>
                    )
                  )}
                  {getStatusBadge(newsletter)}
                  {!inProgress && (
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  )}
                </div>
              </div>

              {/* Expanded details panel (prompt and progress) */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border" onClick={(e) => e.stopPropagation()}>
                  {/* Collapsible Prompt Section */}
                  {activeTaskData?.custom_prompt && (
                    <CollapsiblePrompt prompt={activeTaskData.custom_prompt} />
                  )}
                  {/* Task Progress Panel */}
                  {hasRealTimeProgress && (
                    <TaskProgressPanel progress={realtimeProgress} compact />
                  )}
                </div>
              )}
            </Card>
          );})}
        </div>
      )}

      {/* Newsletter Detail Modal */}
      {selectedNewsletter && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto" onClick={() => { setSelectedNewsletter(null); setShowShareMenu(false); }}>
          <div className="bg-surface border border-border rounded-xl max-w-2xl w-full my-4 sm:my-0" onClick={(e) => { e.stopPropagation(); setShowShareMenu(false); }}>
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-border">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-bold">{extractTopic(selectedNewsletter)}</h2>
                  <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-text-secondary mt-2">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      {formatFullDateInTimezone(selectedNewsletter.generated_at || selectedNewsletter.created_at, userTimezone)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                      {formatTimeInTimezone(selectedNewsletter.generated_at || selectedNewsletter.created_at, userTimezone)}
                    </span>
                    {selectedNewsletter.duration_seconds && (
                      <span className="flex items-center gap-1" title="Generation time">
                        <Timer className="w-3 h-3 sm:w-4 sm:h-4" />
                        {formatDuration(selectedNewsletter.duration_seconds)}
                      </span>
                    )}
                    {getStatusBadge(selectedNewsletter)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedNewsletter(null)} title="Close" className="flex-shrink-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {isLoadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                  <span className="ml-2 text-text-secondary text-sm">Loading details...</span>
                </div>
              ) : isStaleInProgress(selectedNewsletter) ? (
                // Show error message for stale in-progress newsletters
                <div className="text-center py-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-error" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Generation Issue Detected</h3>
                  <p className="text-text-secondary text-sm mb-4">
                    We encountered an issue while generating your newsletter. Our team has been automatically notified and is working to resolve this.
                  </p>
                  <p className="text-xs sm:text-sm text-text-muted">
                    You can try generating a new newsletter from the dashboard. We apologize for any inconvenience.
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-text-secondary mb-2">Summary</h3>
                    <p className="text-text-primary text-sm sm:text-base leading-relaxed">
                      {selectedNewsletter.content_summary || selectedNewsletter.subject || 'No summary available.'}
                    </p>
                  </div>

                  {/* See Full Report Button */}
                  {selectedNewsletter.s3_url ? (
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={handleOpenFullReport}
                      leftIcon={<ExternalLink className="w-4 h-4" />}
                    >
                      See Full Report
                    </Button>
                  ) : (
                    <div className="text-center py-4 text-text-secondary bg-surface-light rounded-lg">
                      <FileText className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs sm:text-sm">Full report not available</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-border mt-2 pt-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleDownload} disabled={!selectedNewsletter.s3_url} title="Download">
                  <Download className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleEmailShare} title="Email">
                  <Mail className="w-4 h-4" />
                </Button>
                {/* Share dropdown */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => setShowShareMenu(!showShareMenu)} title="Share">
                    <Share2 className="w-4 h-4" />
                  </Button>
                  {showShareMenu && (
                    <div className="absolute left-0 bottom-full mb-1 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-20">
                      <button
                        onClick={() => { handleTwitterShare(); setShowShareMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-surface-light flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        Share on X
                      </button>
                      <button
                        onClick={() => { handleLinkedInShare(); setShowShareMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-surface-light flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        Share on LinkedIn
                      </button>
                      <button
                        onClick={() => { handleFacebookShare(); setShowShareMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-surface-light flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Share on Facebook
                      </button>
                      <hr className="my-1 border-border" />
                      <button
                        onClick={() => { handleShare(); setShowShareMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-surface-light flex items-center gap-2"
                      >
                        <Share2 className="w-4 h-4" />
                        Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setSelectedNewsletter(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

